import { c } from 'ttag';

import type { PrivateKeyReference, PublicKeyReference } from '@proton/crypto';
import { CryptoProxy, VERIFICATION_STATUS } from '@proton/crypto';
import { arrayToHexString } from '@proton/crypto/lib/utils';
import { fetchSignedKeyLists } from '@proton/key-transparency/lib';
import { getAndVerifyApiKeys } from '@proton/shared/lib/api/helpers/getAndVerifyApiKeys';
import { decryptKeyPacket, encryptAndSignKeyPacket } from '@proton/shared/lib/keys/keypacket';
import { encryptMemberToken } from '@proton/shared/lib/keys/memberToken';
import noop from '@proton/utils/noop';

import { setupKeys } from '../api/keys';
import type {
    Address,
    Api,
    KTUserContext,
    Member,
    MemberInvitationData,
    MemberReadyForUnprivatization,
    MemberReadyForUnprivatizationApproval,
    MemberUnprivatization,
    MemberUnprivatizationAcceptState,
    MemberUnprivatizationAutomaticApproveState,
    MemberUnprivatizationManualApproveState,
    MemberUnprivatizationOutput,
    PrivateMemberUnprivatizationOutput,
    PublicMemberUnprivatizationOutput,
    Unwrap,
    VerifyOutboundPublicKeys,
} from '../interfaces';
import { MemberUnprivatizationState } from '../interfaces';
import { srpVerify } from '../srp';
import { getVerifiedPublicKeys, validateOrganizationKeySignature } from './organizationKeys';
import type { ResetAddressKeysPayload } from './resetKeys';

export const MEMBER_SIGNATURE_CONTEXT = {
    INVITATION_DATA_SIGNATURE_CONTEXT: 'account.unprivatization-invitation-data',
    KEY_TOKEN_SIGNATURE_CONTEXT: 'account.key-token.user-unprivatization',
};

export const parseInvitationData = (data: string): MemberInvitationData => {
    return JSON.parse(data);
};

export const serializeInvitationData = (data: MemberInvitationData) => {
    return JSON.stringify(data);
};

export const getInvitationData = async ({
    address,
    api,
    expectRevisionChange,
}: {
    address: string;
    api: Api;
    expectRevisionChange?: boolean;
}) => {
    let revision = 1;
    try {
        const result = await fetchSignedKeyLists(api, 0, address);
        const last = result[result.length - 1];
        revision = last.Revision + (expectRevisionChange ? 1 : 0);
    } catch {}
    return serializeInvitationData({
        Address: address,
        Revision: revision,
    });
};

export const getSignedInvitationData = async (signingKey: PrivateKeyReference, textData: string) => {
    return CryptoProxy.signMessage({
        textData, // stripTrailingSpaces: false,
        signingKeys: [signingKey],
        detached: true,
        signatureContext: {
            value: MEMBER_SIGNATURE_CONTEXT.INVITATION_DATA_SIGNATURE_CONTEXT,
            critical: true,
        },
    });
};

const generateActivationToken = () => {
    const randomBytes = crypto.getRandomValues(new Uint8Array(32));
    return arrayToHexString(randomBytes);
};

const getDecryptedOrganizationActivationToken = async ({
    armoredMessage,
    verificationKeys,
    decryptionKeys,
}: {
    armoredMessage: string;
    verificationKeys: PublicKeyReference[] | null;
    decryptionKeys: PrivateKeyReference[];
}) => {
    const { data: decryptedToken, verified } = await CryptoProxy.decryptMessage({
        armoredMessage,
        decryptionKeys,
        // No verification in Global SSO case
        ...(verificationKeys
            ? {
                  verificationKeys,
                  signatureContext: {
                      value: MEMBER_SIGNATURE_CONTEXT.KEY_TOKEN_SIGNATURE_CONTEXT,
                      required: true,
                  },
              }
            : {}),
    });

    if (verificationKeys !== null && verified !== VERIFICATION_STATUS.SIGNED_AND_VALID) {
        const error = new Error(c('Error').t`Signature verification failed`);
        error.name = 'SignatureError';
        throw error;
    }

    return decryptedToken;
};

const getEncryptedOrganizationActivationToken = async ({
    token,
    signingKeys,
    encryptionKeys,
}: {
    token: string;
    signingKeys: PrivateKeyReference[];
    encryptionKeys: PublicKeyReference[];
}) => {
    const { message } = await CryptoProxy.encryptMessage({
        textData: token,
        encryptionKeys,
        signingKeys,
        signatureContext: { value: MEMBER_SIGNATURE_CONTEXT.KEY_TOKEN_SIGNATURE_CONTEXT, critical: true },
    });
    return message;
};

export const validateInvitationData = async ({
    textData,
    armoredSignature,
    verificationKeys,
}: {
    textData: string;
    armoredSignature: string;
    verificationKeys: PublicKeyReference[];
}) => {
    const result = await CryptoProxy.verifyMessage({
        armoredSignature,
        textData,
        verificationKeys,
        signatureContext: { value: MEMBER_SIGNATURE_CONTEXT.INVITATION_DATA_SIGNATURE_CONTEXT, required: true },
    });

    if (result.verified !== VERIFICATION_STATUS.SIGNED_AND_VALID) {
        const error = new Error(c('Error').t`Signature verification failed`);
        error.name = 'SignatureError';
        throw error;
    }

    return result;
};

export const validateInvitationDataValues = async ({
    invitationData,
    invitationAddress,
    options,
}: {
    invitationData: MemberInvitationData;
    invitationAddress: Address;
    options: {
        validateRevision: boolean;
        newMemberCreation: boolean;
    };
}) => {
    if (options.validateRevision) {
        const revision = (invitationAddress.SignedKeyList?.Revision ?? 0) + (options.newMemberCreation ? 1 : 0);
        if (revision !== invitationData.Revision) {
            throw new Error('Invalid invitation signed key list revision state');
        }
    }
};

export const parseUnprivatizationData = async ({
    unprivatizationData,
    addresses,
}: {
    unprivatizationData: MemberUnprivatizationOutput;
    addresses: Address[];
}): Promise<
    | { type: 'private'; payload: { unprivatizationData: PrivateMemberUnprivatizationOutput } }
    | {
          type: 'public';
          payload: {
              orgPublicKey: PublicKeyReference;
              invitationData: MemberInvitationData;
              unprivatizationData: PublicMemberUnprivatizationOutput;
              invitationAddress: Address;
          };
      }
    | {
          type: 'gsso';
          payload: {
              orgPublicKey: PublicKeyReference;
              unprivatizationData: PublicMemberUnprivatizationOutput;
              invitationAddress: Address;
          };
      }
> => {
    if (unprivatizationData.PrivateIntent) {
        return {
            type: 'private',
            payload: {
                unprivatizationData,
            },
        };
    }
    const { OrgPublicKey, InvitationData } = unprivatizationData;
    const orgPublicKey = await CryptoProxy.importPublicKey({ armoredKey: OrgPublicKey });

    if (InvitationData === null) {
        const invitationAddress = addresses.at(0);
        if (!invitationAddress) {
            throw new Error('Invalid invitation address');
        }
        return {
            type: 'gsso',
            payload: {
                orgPublicKey,
                invitationAddress,
                unprivatizationData,
            },
        };
    }

    const invitationData = parseInvitationData(InvitationData);
    const invitationEmail = invitationData.Address;

    const invitationAddress = addresses.find(({ Email }) => Email === invitationEmail);
    if (!invitationAddress) {
        throw new Error('Invalid invitation address');
    }

    return {
        type: 'public',
        payload: {
            orgPublicKey,
            invitationData,
            invitationAddress,
            unprivatizationData,
        },
    };
};
export type ParsedUnprivatizationData = Unwrap<ReturnType<typeof parseUnprivatizationData>>;

export const validateUnprivatizationData = async ({
    api,
    verifyOutboundPublicKeys,
    userContext,
    parsedUnprivatizationData,
    options,
}: {
    api: Api;
    verifyOutboundPublicKeys: VerifyOutboundPublicKeys;
    userContext: KTUserContext | undefined;
    parsedUnprivatizationData: ParsedUnprivatizationData;
    options: Parameters<typeof validateInvitationDataValues>[0]['options'];
}) => {
    if (parsedUnprivatizationData.type === 'private') {
        return;
    }

    const {
        orgPublicKey,
        unprivatizationData: { AdminEmail, InvitationData, InvitationSignature, OrgKeyFingerprintSignature },
        invitationAddress,
    } = parsedUnprivatizationData.payload;
    const verifiedPublicKeysResult = await getVerifiedPublicKeys({
        api,
        verifyOutboundPublicKeys,
        email: AdminEmail,
        userContext,
    }).catch(noop);
    if (!verifiedPublicKeysResult) {
        throw new Error('Unable to fetch public keys of admin');
    }

    const verifiedPublicKeys = verifiedPublicKeysResult.map(({ publicKey }) => publicKey);

    await validateOrganizationKeySignature({
        organizationKey: orgPublicKey,
        armoredSignature: OrgKeyFingerprintSignature,
        verificationKeys: verifiedPublicKeys,
    });

    // There is no invitation data in the global sso case so we can stop here
    if (parsedUnprivatizationData.type === 'gsso') {
        return;
    }

    await validateInvitationData({
        textData: InvitationData,
        armoredSignature: InvitationSignature,
        verificationKeys: [orgPublicKey],
    });

    const invitationData = parsedUnprivatizationData.payload.invitationData;

    await validateInvitationDataValues({
        invitationAddress,
        invitationData,
        options,
    });
};

export const acceptUnprivatization = async ({
    userKeys,
    addressKeys,
    parsedUnprivatizationData,
}: {
    parsedUnprivatizationData: ParsedUnprivatizationData;
    userKeys: PrivateKeyReference[];
    addressKeys: PrivateKeyReference[];
}) => {
    if (parsedUnprivatizationData.type === 'private') {
        return;
    }
    const token = generateActivationToken();
    const { orgPublicKey } = parsedUnprivatizationData.payload;
    const orgActivationToken = await getEncryptedOrganizationActivationToken({
        token,
        encryptionKeys: [orgPublicKey],
        signingKeys: addressKeys,
    });
    const orgPrimaryUserKeysArmored = await Promise.all(
        userKeys.map((userKey) => {
            return CryptoProxy.exportPrivateKey({
                privateKey: userKey,
                passphrase: token,
            });
        })
    );
    return {
        OrgUserKeys: orgPrimaryUserKeysArmored,
        OrgActivationToken: orgActivationToken,
    };
};

export const setupKeysWithUnprivatization = async ({
    payload,
    api,
    password,
    parsedUnprivatizationData,
}: {
    payload: Omit<ResetAddressKeysPayload, 'onSKLPublishSuccess'> & {
        salt: string;
        encryptedDeviceSecret?: string;
    };
    api: Api;
    password: string;
    parsedUnprivatizationData: ParsedUnprivatizationData;
}) => {
    const product = 'generic';

    if (parsedUnprivatizationData.type === 'public' || parsedUnprivatizationData.type === 'gsso') {
        const { orgPublicKey } = parsedUnprivatizationData.payload;
        const token = generateActivationToken();
        const primaryUserKey = payload.privateKeys.userKey;
        const addressKeys = payload.privateKeys.addressKeys.map(({ privateKey }) => privateKey);
        const orgActivationToken = await getEncryptedOrganizationActivationToken({
            token,
            encryptionKeys: [orgPublicKey],
            signingKeys: addressKeys,
        });
        const orgPrimaryUserKeyArmored = await CryptoProxy.exportPrivateKey({
            privateKey: primaryUserKey,
            passphrase: token,
        });

        const input = {
            KeySalt: payload.salt,
            PrimaryKey: payload.userKeyPayload,
            AddressKeys: payload.addressKeysPayload,
            OrgActivationToken: orgActivationToken,
            OrgPrimaryUserKey: orgPrimaryUserKeyArmored,
            ...(payload.encryptedDeviceSecret ? { EncryptedSecret: payload.encryptedDeviceSecret } : {}),
        } as const;

        await srpVerify({
            api,
            credentials: { password },
            config: setupKeys(input, product),
        });
    } else if (parsedUnprivatizationData.type === 'private') {
        await srpVerify({
            api,
            credentials: { password },
            config: setupKeys(
                {
                    KeySalt: payload.salt,
                    PrimaryKey: payload.userKeyPayload,
                    AddressKeys: payload.addressKeysPayload,
                    ...(payload.encryptedDeviceSecret ? { EncryptedSecret: payload.encryptedDeviceSecret } : {}),
                },
                product
            ),
        });
    } else {
        throw new Error('Unknown type');
    }
};

export const reencryptAddressKeyToken = async ({
    Token,
    decryptionKeys,
    encryptionKey,
    signingKey,
}: {
    Token: string;
    decryptionKeys: PrivateKeyReference[];
    encryptionKey: PublicKeyReference;
    signingKey: PrivateKeyReference;
}) => {
    const { sessionKey, message } = await decryptKeyPacket({ armoredMessage: Token, decryptionKeys });
    return encryptAndSignKeyPacket({
        sessionKey,
        binaryData: message.data,
        encryptionKey,
        signingKey,
    });
};

interface UnprivatizeMemberUserKeyDto {
    OrgPrivateKey: string;
    OrgToken: string;
}

interface UnprivatizeMemberAddressKeyDto {
    AddressKeyID: string;
    OrgSignature: string;
    OrgTokenKeyPacket: string;
}

export interface UnprivatizeMemberResult {
    UserKeys: UnprivatizeMemberUserKeyDto[];
    AddressKeys: UnprivatizeMemberAddressKeyDto[];
}

export const getIsMemberUnprivatizationInAutomaticApproveState = (
    unprivatizationData: MemberUnprivatization | null
): unprivatizationData is MemberUnprivatizationAutomaticApproveState => {
    return Boolean(
        unprivatizationData?.State === MemberUnprivatizationState.Ready &&
            !unprivatizationData.PrivateIntent &&
            unprivatizationData.InvitationData &&
            unprivatizationData.InvitationSignature &&
            unprivatizationData.ActivationToken &&
            (unprivatizationData.PrivateKeys?.length || 0) > 0
    );
};

export const getIsMemberUnprivatizationInManualApproveState = (
    unprivatizationData: MemberUnprivatization | null
): unprivatizationData is MemberUnprivatizationManualApproveState => {
    return Boolean(
        unprivatizationData?.State === MemberUnprivatizationState.Ready &&
            !unprivatizationData.PrivateIntent &&
            !unprivatizationData.InvitationData &&
            !unprivatizationData.InvitationSignature &&
            unprivatizationData.ActivationToken &&
            (unprivatizationData.PrivateKeys?.length || 0) > 0
    );
};

export const getIsMemberUnprivatizationInManualAcceptState = (
    unprivatizationData: MemberUnprivatization | null
): unprivatizationData is MemberUnprivatizationAcceptState => {
    return Boolean(
        unprivatizationData?.State === MemberUnprivatizationState.Pending &&
            !unprivatizationData.PrivateIntent &&
            unprivatizationData.InvitationData &&
            unprivatizationData.InvitationSignature &&
            !unprivatizationData.ActivationToken &&
            !unprivatizationData.PrivateKeys?.length
    );
};

export const getIsMemberInAutomaticApproveState = (member: Member): member is MemberReadyForUnprivatization => {
    return getIsMemberUnprivatizationInAutomaticApproveState(member.Unprivatization);
};

export const getIsMemberInManualApproveState = (member: Member): member is MemberReadyForUnprivatizationApproval => {
    return getIsMemberUnprivatizationInManualApproveState(member.Unprivatization);
};

export const getIsMemberInManualAcceptState = (member: Member): member is MemberReadyForUnprivatizationApproval => {
    return getIsMemberUnprivatizationInManualAcceptState(member.Unprivatization);
};

export const unprivatizeMemberHelper = async ({
    data: { ActivationToken, PrivateKeys },
    verificationKeys,
    organizationKey,
    memberAddresses,
}: {
    data: {
        ActivationToken: string;
        PrivateKeys: string[];
    };
    memberAddresses: Address[];
    verificationKeys: PublicKeyReference[] | null;
    organizationKey?: PrivateKeyReference;
}) => {
    if (!organizationKey) {
        throw new Error('Invalid requirements');
    }
    const token = await getDecryptedOrganizationActivationToken({
        armoredMessage: ActivationToken,
        decryptionKeys: [organizationKey],
        verificationKeys,
    });
    const userPrivateKeys = await Promise.all(
        PrivateKeys.map((privateKey) => {
            return CryptoProxy.importPrivateKey({
                armoredKey: privateKey,
                passphrase: token,
            });
        })
    );

    const newMemberKeyToken = generateActivationToken();
    const armoredUserPrivateKeys = await Promise.all(
        userPrivateKeys.map((userPrivateKey) => {
            return CryptoProxy.exportPrivateKey({
                privateKey: userPrivateKey,
                passphrase: newMemberKeyToken,
            });
        })
    );
    const organizationToken = await encryptMemberToken(newMemberKeyToken, organizationKey);

    const UserKeys = armoredUserPrivateKeys.map(
        (armoredUserPrivateKey): UnprivatizeMemberUserKeyDto => ({
            OrgPrivateKey: armoredUserPrivateKey,
            OrgToken: organizationToken,
        })
    );

    const AddressKeys = (
        await Promise.all(
            memberAddresses.map(async (memberAddress) => {
                const result = await Promise.all(
                    memberAddress.Keys.map(async (memberAddressKey): Promise<UnprivatizeMemberAddressKeyDto> => {
                        if (!memberAddressKey.Token) {
                            throw new Error('Missing token');
                        }
                        const result = await reencryptAddressKeyToken({
                            Token: memberAddressKey.Token,
                            decryptionKeys: userPrivateKeys,
                            encryptionKey: organizationKey,
                            signingKey: organizationKey,
                        });
                        return {
                            AddressKeyID: memberAddressKey.ID,
                            OrgSignature: result.signature,
                            OrgTokenKeyPacket: result.keyPacket,
                        };
                    })
                );
                return result;
            })
        )
    ).flat();

    return {
        UserKeys,
        AddressKeys,
    };
};

export class UnprivatizationRevisionError extends Error {}

export const getUnprivatizeMemberPayload = async ({
    verifyOutboundPublicKeys,
    api,
    member,
    memberAddresses,
    organizationKey,
    options = {
        ignoreRevisionCheck: false,
    },
}: {
    api: Api;
    member: MemberReadyForUnprivatization;
    memberAddresses: Address[];
    organizationKey?: PrivateKeyReference;
    verifyOutboundPublicKeys: VerifyOutboundPublicKeys;
    options?: {
        ignoreRevisionCheck: boolean;
    };
}): Promise<UnprivatizeMemberResult> => {
    if (!organizationKey) {
        throw new Error('Invalid requirements');
    }
    const unprivatizationData = member.Unprivatization;
    const { InvitationData, InvitationSignature, ActivationToken, PrivateKeys } = unprivatizationData;
    await validateInvitationData({
        textData: InvitationData,
        armoredSignature: InvitationSignature,
        verificationKeys: [organizationKey],
    });
    const data = parseInvitationData(InvitationData);

    const verifiedApiKeys = await getAndVerifyApiKeys({
        api,
        email: data.Address,
        verifyOutboundPublicKeys,
        internalKeysOnly: false,
        noCache: true,
    }).catch(noop);

    if (!verifiedApiKeys) {
        throw new Error('Unable to fetch public keys');
    }
    if (!options.ignoreRevisionCheck && verifiedApiKeys.Address.SignedKeyList?.Revision !== data.Revision) {
        throw new UnprivatizationRevisionError('Unexpected revision');
    }
    const verifiedPublicKeys = verifiedApiKeys.addressKeys.map(({ publicKey }) => publicKey);

    return unprivatizeMemberHelper({
        data: {
            ActivationToken,
            PrivateKeys,
        },
        memberAddresses,
        verificationKeys: verifiedPublicKeys,
        organizationKey,
    });
};
